
import { useState } from 'react';
import { featuredAds, adCategories } from '../../../mocks/localAds';

export default function AdDirectory() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filtered =
    selectedCategory === 'all'
      ? featuredAds
      : featuredAds.filter((ad) => ad.category === selectedCategory);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Business Directory</h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Browse our community of trusted local businesses serving Roseville and Placer County
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {adCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-5 py-2 rounded-full font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm ${
                selectedCategory === cat.id
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <i className={cat.icon}></i>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Business Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((ad) => (
            <article
              key={ad.id}
              className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border group ${
                ad.featured ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'
              }`}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={ad.image}
                  alt={`${ad.name} - local business Roseville California`}
                  title={`${ad.name} Roseville CA`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                {ad.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-xs"></i>
                      Featured
                    </span>
                  </div>
                )}
                {ad.offer && (
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-teal-700 text-xs font-bold rounded-lg">
                      {ad.offer}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${ad.logo} text-teal-600 text-xl`}></i>
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      <a
                        href={ad.website}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="cursor-pointer"
                      >
                        {ad.name}
                      </a>
                    </h4>
                    <p className="text-sm text-gray-500 italic">{ad.tagline}</p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{ad.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-map-pin-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                    <span className="truncate">{ad.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-phone-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                    <span>{ad.phone}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                    <span className="text-sm font-semibold text-gray-900">{ad.rating}</span>
                    <span className="text-xs text-gray-400">({ad.reviews} reviews)</span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        ad.name + ' ' + ad.address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                      title="Get Directions"
                    >
                      <i className="ri-navigation-line text-gray-600 text-sm"></i>
                    </a>
                    <a
                      href={ad.website}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="w-9 h-9 rounded-lg bg-teal-50 hover:bg-teal-100 flex items-center justify-center transition-colors cursor-pointer"
                      title="Visit Website"
                    >
                      <i className="ri-external-link-line text-teal-600 text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-store-2-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500">No businesses found in this category yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
